const CHART_DIMENSIONS = {
  width: 920,
  height: 420,
  padding: {
    top: 32,
    right: 96,
    bottom: 32,
    left: 128,
  },
}

function toNumber(value) {
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function calcYTicks(min, max, count = 5) {
  if (count <= 1) return [min]
  const span = max - min
  if (span <= 0) return [min]

  const step = span / (count - 1)
  const ticks = []
  for (let i = 0; i < count; i += 1) {
    ticks.push(min + step * i)
  }
  return ticks
}

/**
 * Build normalized data for SVG step chart.
 * Path is extended to rangeStart/rangeEnd even when there is no entry exactly
 * on those timestamps. Circles are rendered only for real entries.
 *
 * @param {object} params
 * @param {Array} params.entries chronological entries with date and prices
 * @param {number} [params.rangeStart] timestamp in ms
 * @param {number} [params.rangeEnd] timestamp in ms
 * @param {'price_brutto'|'price_m2_brutto'} [params.priceKey]
 * @returns {object|null}
 */
export function buildPriceChartData({
  entries,
  rangeStart,
  rangeEnd,
  priceKey = 'price_brutto',
}) {
  if (!Array.isArray(entries) || entries.length === 0) return null

  const normalized = entries
    .map((entry) => {
      const timestamp = Date.parse(entry.date)
      if (!Number.isFinite(timestamp)) return null

      const mainPrice = toNumber(entry.price_brutto)
      const m2Price = toNumber(entry.price_m2_brutto)
      const selectedRaw = priceKey === 'price_m2_brutto' ? m2Price : mainPrice
      if (!Number.isFinite(selectedRaw) || selectedRaw <= 0) return null

      return {
        date: entry.date,
        timestamp,
        price_brutto: mainPrice,
        price_m2_brutto: m2Price,
        selectedPrice: selectedRaw,
      }
    })
    .filter(Boolean)
    .sort((a, b) => a.timestamp - b.timestamp)

  if (normalized.length === 0) return null

  const firstTs = normalized[0].timestamp
  const lastTs = normalized[normalized.length - 1].timestamp

  const rawStart = Number.isFinite(rangeStart) ? rangeStart : firstTs
  const rawEnd = Number.isFinite(rangeEnd) ? rangeEnd : lastTs
  const start = Math.max(firstTs, Math.min(rawStart, rawEnd))
  const end = Math.max(start, Math.min(Math.max(rawStart, rawEnd), Date.now()))

  const startSource =
    [...normalized].reverse().find((entry) => entry.timestamp <= start) ?? normalized[0]
  const endSource =
    [...normalized].reverse().find((entry) => entry.timestamp <= end) ?? normalized[normalized.length - 1]

  if (!startSource || !endSource) return null

  const pointEntries = normalized.filter(
    (entry) => entry.timestamp >= start && entry.timestamp <= end,
  )

  const pathEntries = [
    {
      ...startSource,
      timestamp: start,
      date: new Date(start).toISOString(),
      isBoundary: true,
    },
    ...pointEntries.filter((entry) => entry.timestamp > start && entry.timestamp < end),
    {
      ...endSource,
      timestamp: end,
      date: new Date(end).toISOString(),
      isBoundary: true,
    },
  ]

  const dedupPathEntries = []
  for (const entry of pathEntries) {
    const last = dedupPathEntries[dedupPathEntries.length - 1]
    if (!last || last.timestamp !== entry.timestamp || last.selectedPrice !== entry.selectedPrice) {
      dedupPathEntries.push(entry)
    }
  }

  const dateSpan = Math.max(1, end - start)

  const selectedPrices = dedupPathEntries.map((p) => p.selectedPrice)
  const minPrice = Math.min(...selectedPrices)
  const maxPrice = Math.max(...selectedPrices)
  const priceSpan = Math.max(1, maxPrice - minPrice)

  const { width, height, padding } = CHART_DIMENSIONS
  const innerWidth = width - padding.left - padding.right
  const innerHeight = height - padding.top - padding.bottom

  const mapX = (timestamp) => {
    const ratio = (timestamp - start) / dateSpan
    return padding.left + ratio * innerWidth
  }

  const mapY = (price) => {
    const ratio = (price - minPrice) / priceSpan
    return height - padding.bottom - ratio * innerHeight
  }

  const points = pointEntries.map((entry) => ({
    ...entry,
    x: mapX(entry.timestamp),
    y: mapY(entry.selectedPrice),
  }))

  const stepPoints = dedupPathEntries.map((entry) => ({
    ...entry,
    x: mapX(entry.timestamp),
    y: mapY(entry.selectedPrice),
  }))

  let stepPath = ''
  if (stepPoints.length <= 1) {
    const p = stepPoints[0] ?? {
      y: mapY(startSource.selectedPrice),
    }
    stepPath = `M ${padding.left} ${p.y} L ${width - padding.right} ${p.y}`
  } else {
    stepPath = `M ${stepPoints[0].x} ${stepPoints[0].y}`
    for (let i = 1; i < stepPoints.length; i += 1) {
      const prev = stepPoints[i - 1]
      const curr = stepPoints[i]
      stepPath += ` L ${curr.x} ${prev.y} L ${curr.x} ${curr.y}`
    }
  }

  const yTicks = calcYTicks(minPrice, maxPrice, 5).map((value) => ({
    value,
    y: mapY(value),
  }))

  const now = Date.now()
  const showToday = now >= start && now <= end
  const todayMarker = showToday
    ? {
        x: mapX(now),
      }
    : null

  return {
    width,
    height,
    padding,
    points,
    stepPath,
    yTicks,
    todayMarker,
    startDate: new Date(start).toISOString(),
    endDate: new Date(end).toISOString(),
  }
}
